import React from "react";
import { Icon, Input, Text, Tooltip } from "@showitinc/showit-wc";
import AppConstants from "../../../../constants/AppConstants";
import { INPUT_TYPE_NUMBER } from "../../../../constants/ElementConstants";
import { LINE_THICKNESS } from "../../../../constants/MergeConstants";
import Styles from "./SizeAndPosition.module.css";
import DataTypes from "@/constants/DataTypes";

const { ELEMENT_TYPE_LINE, MAX_ELEMENT_HEIGHT, MAX_ELEMENT_WIDTH } = AppConstants;

const limitDecimals = (num, decimals = 2) => Number(num.toFixed(decimals));

const SizeAndPositionInputs = React.memo(
  ({ onChange, locked, position, type, onBlur }) => {
    const x = limitDecimals(position.get("x"));
    const y = limitDecimals(position.get("y"));
    const w = limitDecimals(position.get("w"));
    const h = limitDecimals(position.get("h"));
    const a = limitDecimals(position.get("a"));
    const minHeight = position.get("minHeight");
    const minWidth = position.get("minWidth");

    const blurProps = onBlur
      ? { blurOnEscape: true, blurOnEnter: true, emitOnBlur: true, onBlur: onBlur }
      : {};

    return (
      <div className="side-by-side">
        <Tooltip position="left">
          <div slot="content">
            <Text>{type === ELEMENT_TYPE_LINE ? "Length" : "Width"}</Text>
          </div>
          <Input
            {...blurProps}
            label={type === ELEMENT_TYPE_LINE ? "L" : "W"}
            type={INPUT_TYPE_NUMBER}
            disabled={locked || type === DataTypes.GROUP}
            data-testid="size-and-position-width-input"
            min={minWidth ?? 1}
            max={MAX_ELEMENT_WIDTH}
            step="1"
            value={w}
            units="px"
            onSIBeforeChange={onChange("w", position.get("w"))}
            onSIInput={onChange("w", position.get("w"), false)}
          />
        </Tooltip>

        <Input
          {...blurProps}
          label="X"
          type={INPUT_TYPE_NUMBER}
          data-testid="size-and-position-x-input"
          step="1"
          value={x}
          disabled={locked}
          units="px"
          onSIInput={onChange("x", position.get("x"), false)}
          onSIBeforeChange={onChange("x", position.get("x"))}
        />

        <Tooltip position="left">
          <div slot="content">
            <Text>{type === ELEMENT_TYPE_LINE ? "Weight" : "Height"}</Text>
          </div>
          <Input
            {...blurProps}
            label={type === ELEMENT_TYPE_LINE ? "W" : "H"}
            type={INPUT_TYPE_NUMBER}
            data-testid="size-and-position-height-input"
            min={minHeight ?? 1}
            max={MAX_ELEMENT_HEIGHT}
            step="1"
            value={h}
            units="px"
            disabled={locked || type === DataTypes.GROUP}
            onSIInput={onChange(LINE_THICKNESS, position.get("h"), false)}
            onSIBeforeChange={onChange(LINE_THICKNESS, position.get("h"))}
          />
        </Tooltip>

        <Input
          {...blurProps}
          label="Y"
          type={INPUT_TYPE_NUMBER}
          data-testid="size-and-position-y-input"
          step="1"
          value={y}
          disabled={locked}
          units="px"
          onSIInput={onChange("y", position.get("y"), false)}
          onSIBeforeChange={onChange("y", position.get("y"))}
        />

        <Input
          type={INPUT_TYPE_NUMBER}
          step="1"
          value={a}
          disabled={locked}
          data-testid="size-and-position-rotation-input"
          units="&deg;"
          onSIInput={onChange("a", position.get("a"), false)}
          onSIBeforeChange={onChange("a", position.get("a"))}
          includeSlider
          {...blurProps}
          shiftMod={15}
          min="0"
          max="360"
          cycle
        >
          <Icon type="rotation" size="12" slot="label" className={Styles.rotationIcon} />
        </Input>
      </div>
    );
  },
  (prev, next) => prev.position === next.position
);

export default SizeAndPositionInputs;
